import type {
	PortalClientDto,
	PortalClientRelationshipGroup,
	PortalPayAccountsContextAccountDto,
} from '@supply-mover-2-0/shared';
import { nameStartsWithCustomerNumber } from '@supply-mover-2-0/shared';
import { clientDisplayId } from './clientDisplayId';

/** The translation function from `useTranslation()`. */
type TranslateFn = (key: string) => string;

/** One row of the location picker, in the shape the shared Select takes. */
export interface AccountLocationOptionModel {
	value: string;
	label: string;
	subtitle?: string;
	selectedSuffix?: string;
	group?: string;
}

/**
 * Above this many locations the picker gains a search field (PD-15027): a
 * customer with dozens of locations finds one faster by typing part of its
 * name or customer number than by scrolling.
 */
export const ACCOUNT_LOCATION_SEARCH_THRESHOLD = 8;

const GROUP_ORDER: Array<{
	group: PortalClientRelationshipGroup;
	label: string;
}> = [
	{ group: 'direct', label: 'Your Accounts' },
	{ group: 'child', label: 'Child Accounts' },
	{ group: 'ownership', label: 'Ownership Accounts' },
];

/**
 * The options of the storefront location picker, the one on the Invoices,
 * Orders, Statements and Transactions filters.
 *
 * "All Locations" (value '') comes first, then the accessible accounts under
 * three headings, Your Accounts, Child Accounts and Ownership Accounts, each
 * sorted by name (PD-14189). The value is the account id as a string, which
 * is what the pages keep in their `clientId` URL parameter.
 *
 * PD-17381 — locations with similar names are told apart by customer number.
 * The number is the option's second line in the open menu and stays beside
 * the name once chosen (PD-15027), and searching matches it as well as the
 * name. A name that already starts with the number shows it once, and an
 * account without a name is labelled by its number alone.
 */
export function accountLocationOptions(
	t: TranslateFn,
	clients: ReadonlyArray<PortalClientDto>,
): Array<AccountLocationOptionModel> {
	const options: Array<AccountLocationOptionModel> = [
		{ value: '', label: t('All Locations') },
	];

	for (const { group, label } of GROUP_ORDER) {
		const members = clients
			.filter((client) => client.relationshipGroup === group)
			.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
		for (const client of members) {
			options.push({
				value: String(client.id),
				...locationLabel(t, clientDisplayId(client), client.name ?? ''),
				group: t(label),
			});
		}
	}

	return options;
}

/**
 * PD-17691 — the Location filter on Make a Payment (the multi-account view).
 * Like the list-page picker, each account shows its customer number on the
 * option's second line and beside the name once chosen, and searching
 * matches the number as well as the name; HF's locations often trade under
 * the same name. "All locations" (value '') comes first, then the accounts
 * in the order the page lists them. The value is the account id as a string.
 */
export function payAccountLocationOptions(
	t: TranslateFn,
	accounts: ReadonlyArray<
		Pick<
			PortalPayAccountsContextAccountDto,
			'clientId' | 'clientName' | 'clientExternalId'
		>
	>,
): Array<AccountLocationOptionModel> {
	return [
		{ value: '', label: t('All locations') },
		...accounts.map((account) => ({
			value: String(account.clientId),
			...locationLabel(
				t,
				payAccountCustomerNumber(account),
				account.clientName ?? '',
			),
		})),
	];
}

/**
 * PD-17691 — a Make a Payment account's customer number: its external id,
 * else its internal id, the rule statements print (`clientCustomerNumber`).
 */
export function payAccountCustomerNumber(
	account: Pick<
		PortalPayAccountsContextAccountDto,
		'clientId' | 'clientExternalId'
	>,
): string {
	return clientDisplayId({
		id: account.clientId,
		externalId: account.clientExternalId,
	});
}

function locationLabel(
	t: TranslateFn,
	customerNumber: string,
	name: string,
): Pick<AccountLocationOptionModel, 'label' | 'subtitle' | 'selectedSuffix'> {
	const number = customerNumber.trim();
	if (name.trim() === '') {
		return { label: t(number) };
	}
	if (number === '' || nameStartsWithCustomerNumber(name, number)) {
		return { label: t(name) };
	}
	return { label: t(name), subtitle: t(number), selectedSuffix: t(number) };
}
